"use server"

import {redirect} from "next/navigation"

import {backendOptions, establishSession, sessionCookieOptions} from "@/lib/auth"
import {loginBackendSession} from "@/lib/auth/backend"
import type {FieldErrors} from "@/lib/auth/credentials"
import {
  deriveSessionCookie,
  loginErrorMessage,
  safeReturnPath,
  validateLogin,
} from "@/lib/auth/credentials"
import {demoAccounts} from "@/lib/demo"
import {authProvider} from "@/lib/env"

/**
 * Native sign-in (wave 1 of the portal replacement).
 *
 * A Server Action rather than a client `fetch`, for one concrete reason: the
 * session cookie must be written by this origin with attributes this app
 * chooses, and only a Server Function or Route Handler can set a cookie. A
 * browser-side POST through the API bridge would let userapi's own Set-Cookie
 * reach the browser carrying `Domain=COOKIE_DOMAIN` — correct for the backend's
 * origin and wrong for ours in every environment where the two differ, which is
 * all the local ones.
 *
 * The password therefore never reaches the browser's JavaScript at all: it goes
 * from the form to this server and no further.
 */

export interface LoginState {
  readonly ok: boolean
  /** Whole-form failure (wrong credentials, backend down). */
  readonly message?: string
  /** Per-field validation failures, keyed by field name. */
  readonly errors?: FieldErrors
  /** Re-populates the form after a failure, so only the password is retyped. */
  readonly email?: string
}

const DEFAULT_RETURN = "/"

export const loginAction = async (
  _previous: LoginState,
  formData: FormData,
): Promise<LoginState> => {
  const email = String(formData.get("email") ?? "").trim()
  const password = String(formData.get("password") ?? "")
  /*
   * The return target is re-validated here, not merely on the page that
   * rendered the form. A Server Action is a POST endpoint with a generated URL:
   * anyone holding it can call it with any payload, so a `returnUri` that was
   * checked at render time proves nothing about the one that arrives here.
   */
  const returnTo = safeReturnPath(formData.get("returnUri")?.toString(), DEFAULT_RETURN)

  const errors = validateLogin({email, password})
  if (Object.keys(errors).length > 0) {
    return {ok: false, errors, email}
  }

  const cookieOptions = await sessionCookieOptions()

  /*
   * The stub provider has no userapi to call. Rather than disabling the form —
   * which would make the sign-in path unexercisable on a machine with no
   * backend — it issues the token the stub already accepts. It verifies
   * nothing, which is exactly what a stub session is, and AUTH_PROVIDER=stub is
   * never a deployed configuration.
   */
  if (authProvider() === "stub") {
    await establishSession({
      name: cookieOptions.cookieName,
      value: process.env.STUB_SESSION_TOKEN ?? "stub-token",
      maxAge: 60 * 60 * 24,
      expires: undefined,
      sameSite: "lax",
      secure: cookieOptions.secure,
      domain: cookieOptions.domain,
      path: "/",
      httpOnly: true,
    })
    redirect(returnTo)
  }

  const result = await loginBackendSession(backendOptions(), {email, password})

  if (result.status === "unavailable") {
    // Never "check your password" for an outage: that sends people off to reset
    // a password that was never wrong, through a flow on the same backend.
    return {ok: false, email, message: `Sign-in is unavailable: ${result.reason}`}
  }
  if (result.status === "rejected") {
    return {ok: false, email, message: loginErrorMessage(result.httpStatus, result.body)}
  }

  const cookie = deriveSessionCookie(result.setCookies, cookieOptions)
  if (cookie === undefined) {
    /*
     * A 200 with no Set-Cookie. This failure has to be loud: the credentials
     * were accepted, so silently returning to the login form reads as "wrong
     * password" and the publisher will retype it forever. userapi attaches the
     * cookie in `afterRemote('login')`, so an absent one means that hook did
     * not run — a deployment problem, not a user one.
     */
    return {
      ok: false,
      email,
      message:
        "Sign-in was accepted but no session cookie came back, so you are not signed in. " +
        "Report this: userapi's login hook did not set the cookie.",
    }
  }

  await establishSession(cookie)
  // `redirect()` signals by throwing, so it stays outside any try/catch —
  // catching it would turn a successful login into an error message.
  redirect(returnTo)
}

export interface DemoSwitchState {
  readonly ok: boolean
  readonly message?: string
}

/**
 * Sign in as one of the configured demo accounts (lib/demo.ts) — the demo
 * role switcher. The email must be on the DEMO_ACCOUNTS list and the password
 * comes from the server's DEMO_PASSWORD, never from the browser, so with the
 * feature off (a real deployment) this signs in as nobody. Same sign-in as the
 * form above: userapi checks the credentials and sets the session.
 */
export const switchDemoAccountAction = async (
  _previous: DemoSwitchState,
  formData: FormData,
): Promise<DemoSwitchState> => {
  const email = String(formData.get("email") ?? "")
  const account = demoAccounts().find(entry => entry.email === email)
  const password = process.env.DEMO_PASSWORD
  if (account === undefined || password === undefined || authProvider() === "stub") {
    return {ok: false, message: "Demo accounts are not available here."}
  }
  const returnTo = safeReturnPath(formData.get("returnUri")?.toString(), DEFAULT_RETURN)

  const cookieOptions = await sessionCookieOptions()
  const result = await loginBackendSession(backendOptions(), {email, password})
  if (result.status === "unavailable") {
    return {
      ok: false,
      message: `Sign-in is unavailable right now (${result.reason}). Try again in a moment.`,
    }
  }
  if (result.status !== "ok") {
    return {
      ok: false,
      message: `${account.email} was refused. Has the devenv demo seed (db/seed/demo-accounts.sql) been run?`,
    }
  }
  const cookie = deriveSessionCookie(result.setCookies, cookieOptions)
  if (cookie === undefined) return {ok: false, message: "Sign-in returned no session cookie."}

  await establishSession(cookie)
  redirect(returnTo)
}
